import React, { useState, useEffect } from 'react';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import Screen from '../../components/common/Screen';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import Left from '../../components/css/Left';
import Image from '../../components/images/Image';
import { fetchUserInfo, apiRequest } from '../../utils';
import Back from '../../components/common/Back';
import { Link, useNavigate } from 'react-router-dom';
import InputField from '../../components/forms/InputField';
import Toast from '../../components/forms/Toast';

const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [pending, setPending] = useState([]);

  const navigate = useNavigate();
  useEffect(() => {
    fetchUserInfo('/manage-employees', setUserInfo, setLoading, setError, []);
  }, []);

  /* /company-members does all of this and more (CSV upload, tags, houses, the same join
     requests), on every brand. Whenever this session can open it, go there; what stays here is
     the fallback for a session it refuses — no event in it, or a club. */
  useEffect(() => {
    if (userInfo?.roster_screen) navigate('/company-members', { replace: true });
  }, [userInfo, navigate]);

  const isCommunity = Boolean(userInfo?.is_community);
  const memberWord = isCommunity ? t('members') : t('colleagues');

  const loadPending = () => {
    apiRequest('GET', '/join-requests').then((d) => setPending(d?.requests || [])).catch(() => {});
  };
  useEffect(() => {
    if (userInfo?.role === 'contactPerson') loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.role]);

  const [searchQuery, setSearchQuery] = useState('');
  const handleSearchChange = (e) => setSearchQuery(e.target.value.toLowerCase());

  const filteredEmployees = userInfo?.employees?.filter((employee) =>
    employee.full_name.toLowerCase().includes(searchQuery)
  );

  // "Add colleague / member" shares an invite LINK — the joiner fills it in and the
  // contact person approves below. Replaces the old create-and-force-add.
  const shareInvite = async () => {
    try {
      const data = await apiRequest('GET', '/company-invite-link');
      const link = data?.invite_link;
      if (!link) throw new Error('somethingWentWrong');
      // A warm, ready-to-send message so the invitee knows why they got the link.
      const message = t('colleagueInviteShareText');
      if (navigator.share) {
        navigator.share({
          title: isCommunity ? t('inviteMemberTitle') : t('inviteColleagueTitle'),
          text: message,
          url: link,
        }).catch(() => {});
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${message}\n${link}`);
        setError('inviteLinkCopied');
        setShowToast(true);
      } else {
        window.prompt(t('copyLink'), link);
      }
    } catch (err) {
      setError(err.message);
      setShowToast(true);
    }
  };

  const acceptRequest = async (id) => {
    try {
      await apiRequest('POST', '/accept-join', { request_id: id });
      setError(null);
      loadPending();
      window.location.reload();
    } catch (err) { setError(err.message); setShowToast(true); }
  };
  const declineRequest = async (id) => {
    try {
      await apiRequest('POST', '/decline-join', { request_id: id });
      loadPending();
    } catch (err) { setError(err.message); setShowToast(true); }
  };

  const removeEmployee = async (userId) => {
    try {
      await apiRequest('POST', '/delete-employee', { user_id: userId });
      setError(null);
      window.location.reload();
    } catch (error) {
      setError(error.message);
      setShowToast(true);
    }
  };

  const promoteToAdmin = async (userId) => {
    try {
      await apiRequest('POST', '/promote-employee', { user_id: userId });
      setError(null);
      window.location.reload();
    } catch (error) {
      setError(error.message);
      setShowToast(true);
    }
  };

  const demoteFromAdmin = async (userId) => {
    try {
      await apiRequest('POST', '/demote-employee', { user_id: userId });
      setError(null);
      window.location.reload();
    } catch (error) {
      setError(error.message);
      setShowToast(true);
    }
  };

  return (
    <Screen p='3'>
      <div className='top'>
        <Back />
        <Title>{isCommunity ? t('manageMembers') : t('manageColleagues')}</Title>

        {userInfo?.role === 'contactPerson' && (
          <Button theme="primary" className="mt-2" onClick={shareInvite} mb='1'>
            <Icon name="user-add" className="mx-2" theme="light" />
            {isCommunity ? t('addMember') : t('addColleague')}
          </Button>
        )}

        <Toast showToast={showToast} setShowToast={setShowToast} message={t(error)} />

        {/* Pending join requests — accept before anyone gets access. */}
        {userInfo?.role === 'contactPerson' && pending.length > 0 && (
          <>
            <hr className="mb-2" />
            <Left>
              <Title heading='6' my='2' className='mt-2 mb-3'>
                {t('pendingRequests')} ({pending.length})
              </Title>
            </Left>
            {pending.map((req) => (
              <div className='row align-items-center mb-2' key={req.id}>
                <div className='col-7 text-start'>
                  <b>{req.full_name}</b>
                  <p className='text-secondary mb-0 text-break' style={{ fontSize: '0.8rem' }}>
                    {req.email}{req.team_name ? ` · ${req.team_name}` : ''}
                  </p>
                </div>
                <div className='col-5 d-flex justify-content-end' style={{ gap: '0.4rem' }}>
                  <Button p='1' theme='primary' onClick={() => acceptRequest(req.id)}>
                    <Icon name='check' theme='light' /> {t('accept')}
                  </Button>
                  <Button p='1' theme='light' onClick={() => declineRequest(req.id)}>
                    <Icon name='cross' />
                  </Button>
                </div>
              </div>
            ))}
          </>
        )}

        {(userInfo?.admins?.length > 0 || filteredEmployees?.length > 0) && (
          <div className='justify-content-center'>
            <hr className="mb-2" />

            {/* Admins Section (no search) */}
            {userInfo?.admins?.length > 0 && (
              <>
                <Left>
                  <Title heading='6' my='2' className='mt-2 mb-3'>
                    {t('admins')} ({userInfo.admins.length})
                  </Title>
                </Left>
                {userInfo.admins.map((admin) => (
                  <div className="dropdown" key={admin.id}>
                    <Button p='1' mb="1" theme='light' dataBsToggle="dropdown" ariaExpanded={false}>
                      <div className='row'>
                        <div className='col-3 d-flex align-items-center'>
                          <Image url={admin.photo_url || require('./../../assets/images/fallback/user.png')} size='3rem' className='circle mb-1' reload />
                        </div>
                        <div className='col-8 d-flex flex-column justify-content-center' style={{ marginLeft: '-1.5rem' }}>
                          <div className='d-flex align-items-center flex-wrap'>
                            <b className='me-2'>{admin.full_name}</b>
                            {admin.role?.toLowerCase() === 'contactpersoon' && (
                              <i className='text-secondary'>({t("contactPerson")})</i>
                            )}
                          </div>
                          <div>
                            <p className='text-secondary mb-0 text-start text-break'>{admin.email}</p>
                          </div>
                        </div>

                        <div className='col-1 d-flex align-items-center'>
                          <Icon name='menu-dots-vertical' theme='secondary' style={{ marginLeft: '-0.8rem' }} />
                        </div>
                      </div>
                    </Button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <Link className="dropdown-item" to={`/u/${admin.id}`}>
                          <Icon name='user' className='me-1' />
                          {t("viewProfile")}
                        </Link>
                      </li>
                      {admin.role?.toLowerCase() !== 'contactpersoon' && (
                        <li>
                          <button type="button" className="dropdown-item" onClick={() => demoteFromAdmin(admin.id)}>
                            <Icon name='arrow-circle-down' className='me-1' />
                            {t("demoteFromAdmin")}
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
              </>
            )}

            {/* Colleagues / members Section (searchable) */}
            {userInfo?.employees?.length > 0 && (
              <>
                <hr className="mb-2" />
                <Left>
                  <Title heading='6' my='2' className='mt-2 mb-3'>
                    {memberWord} ({filteredEmployees.length})
                  </Title>
                </Left>

                <div className='row align-items-center'>
                  <div className='col-1' style={{ marginTop: '-0.7rem' }}>
                    <Icon name="search" />
                  </div>
                  <div className='col-11'>
                    <InputField
                      placeholder={isCommunity ? t('searchMembers') : t('searchColleagues')}
                      name='searchColleagues'
                      value={searchQuery}
                      onChange={handleSearchChange}
                    >
                      <Icon name="employees" />
                    </InputField>
                  </div>
                </div>

                {filteredEmployees.map((employee) => (
                  <div className="dropdown" key={employee.id}>
                    <Button p='1' mb="1" theme='light' dataBsToggle="dropdown" ariaExpanded={false}>
                      <div className='row'>
                        <div className='col-3 d-flex align-items-center'>
                          <Image url={employee.photo_url || require('./../../assets/images/fallback/user.png')} size='3rem' className='circle mb-1' reload />
                        </div>
                        <div className='col-8 d-flex flex-column justify-content-center text-start' style={{ marginLeft: '-1.5rem' }}>
                          <b>{employee.full_name}</b>
                          <p className='text-secondary mb-0 text-break'>{employee.email}</p>
                        </div>

                        <div className='col-1 d-flex align-items-center'>
                          <Icon name='menu-dots-vertical' theme='secondary' style={{ marginLeft: '-0.8rem' }} />
                        </div>
                      </div>
                    </Button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <Link className="dropdown-item" to={`/u/${employee.id}`}>
                          <Icon name='user' className='me-1' />
                          {t("viewProfile")}
                        </Link>
                      </li>
                      <li>
                        <button type="button" className="dropdown-item" onClick={() => promoteToAdmin(employee.id)}>
                          <Icon name='arrow-circle-up' className='me-1' />
                          {t("promoteToAdmin")}
                        </button>
                      </li>
                      <li>
                        <button type="button" className="dropdown-item" onClick={() => removeEmployee(employee.id)}>
                          <Icon name='cross' className='me-1' />
                          {isCommunity ? t('removeMember') : t('removeColleague')}
                        </button>
                      </li>
                    </ul>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

      </div>

      <div className='bottom'>
        <div className="px-3 pt-3" style={{ backgroundColor: 'white' }}>
          <Button to={`/public-company/${userInfo?.user_company_id}`} className='mb-3'>
            {t("done")}
          </Button>
        </div>
      </div>
    </Screen>
  );
};

export default Page;
